'use client';

import { useSelectedScopes } from '@/hooks/use-selected-scopes';
import { useMemberScopes } from '@/hooks/use-member-scopes';

import { type SidebarNavEntry, SidebarNavigation } from '@slims/ui/organisms';
import { AlertTriangle, ChartLine, ClipboardList, Share2, Sparkle, Users, Zap } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import Link from '@/lib/next/link';
import { usePathname } from '@/lib/next/navigation';
import { ROLE_LEVEL } from '@slims/shared/auth';
import type { UserRole } from '@/lib/utils/tenant';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';

interface NavItem {
	label: string;
	href: string;
	icon: ReactNode;
	minRole?: UserRole;
	onlyForRole?: UserRole;
}

/**
 * Navigazione CER: un solo gruppo, «Navigazione», come nel mock v11. Icone
 * monocromatiche; l'area Admin continua ad aprirsi dal toggle
 * nell'header.
 *
 * L'ordine è quello del mock v11 e non è alfabetico né per ruolo: segue il
 * percorso del dato — quanto se n'è prodotta (Dashboard, Monitoraggio), quanta
 * se ne prevede (Previsione), quanta è condivisa (Energia condivisa), a chi va (MySlims), cosa
 * non torna (Anomalie), cosa se ne rendiconta (Report).
 */
const NAV_ITEMS: NavItem[] = [
	{ label: 'Dashboard', href: '/dashboard', icon: <Zap /> },
	{ label: 'Monitoraggio', href: '/monitoring', icon: <ChartLine /> },
	{ label: 'Previsione', href: '/forecast', icon: <Sparkle /> },
	{ label: 'Energia condivisa', href: '/incentives', icon: <Share2 />, minRole: 'admin' },
	{ label: 'MySlims', href: '/myslims', icon: <Users /> },
	{ label: 'Anomalie', href: '/anomalies', icon: <AlertTriangle />, minRole: 'admin' },
	{ label: 'Report', href: '/reports', icon: <ClipboardList />, minRole: 'admin' },
];

export function AppSidebar() {
	const pathname = usePathname();
	const collapsed = useUIStore((s) => s.sidebarCollapsed);
	const toggleSidebar = useUIStore((s) => s.toggleSidebar);
	const role = useAuthStore((s) => s.role);

	const { scopes } = useSelectedScopes();
	const { scopes: memberScopes } = useMemberScopes();
	const hasMemberScope = memberScopes.length > 0;
	const items = useMemo<SidebarNavEntry[]>(() => {
		if (role === 'member') {
			return ['/myslims', '/dashboard', '/monitoring', '/forecast'].map((href) => {
				const item = NAV_ITEMS.find((entry) => entry.href === href)!;
				return { label: item.label, href: item.href, icon: item.icon };
			});
		}
		const userLevel = role ? ROLE_LEVEL[role] : 0;
		return NAV_ITEMS.filter((item) => {
			if (item.href === '/myslims' && !hasMemberScope) return false;
			if (item.href === '/reports' && !scopes.some((s) => s.features?.reports)) return false;
			if (item.href === '/anomalies' && !scopes.some((s) => s.features?.anomalies)) return false;
			if (item.onlyForRole) return role === item.onlyForRole;
			return !item.minRole || userLevel >= ROLE_LEVEL[item.minRole];
		}).map((item) => ({ label: item.label, href: item.href, icon: item.icon }));
	}, [role, scopes, hasMemberScope]);

	return (
		<SidebarNavigation
			linkComponent={Link}
			items={items}
			activeId={pathname}
			matchPrefix
			collapsed={collapsed}
			onToggleCollapse={toggleSidebar}
			copyrightName="InBlenda"
		/>
	);
}
